/**
 * DiameterMeasurement — holds the active study's diameter sources and computes
 * diameters from them (stent-planning panel labels, plane-index filter rings,
 * orthogonal viewport label).
 */

import { buildCenterlinesPolyData } from '../js/CenterlineUtils';
import { computeDiameterAtIndex } from './DiameterCompute';
import { newReslice } from '../js/MaskReslice';
import { CenterlineType, tryConvertMasterToDistanceBranchIndex } from '../CenterlineMeasurement/CenterlinePlaneShared';

let currentSource = null;

// Single reslice instance reused across all diameter computations.
let sharedReslice = null;
function getSharedReslice() {
    if (!sharedReslice) sharedReslice = newReslice();
    return sharedReslice;
}

export function clearSources() {
    currentSource = null;
}

/**
 * Load mask volumes and build per-branch polydatas for a study.
 * Safe to call multiple times; the image loader caches volumes by file key.
 *
 * @param {object} args
 * @param {string} args.studyId
 * @param {object} args.imageLoader
 * @param {object|null} args.wallCenterline - master wall centerline polydata.
 * @param {object|null} args.lumenCenterline - master lumen centerline polydata.
 * @param {File[]|null} args.wallMaskFiles
 * @param {File[]|null} args.lumenMaskFiles
 * @returns {Promise<void>}
 */
export async function ensureSources({
    studyId,
    imageLoader,
    wallCenterline,
    lumenCenterline,
    wallMaskFiles,
    lumenMaskFiles,
}) {
    if (!studyId || !imageLoader) return;

    const [wallMask, lumenMask] = await Promise.all([
        wallMaskFiles && wallMaskFiles.length
            ? imageLoader.tryGetMask(wallMaskFiles)
            : Promise.resolve(null),
        lumenMaskFiles && lumenMaskFiles.length
            ? imageLoader.tryGetMask(lumenMaskFiles)
            : Promise.resolve(null),
    ]);

    const wallBuilt = wallCenterline ? buildCenterlinesPolyData(wallCenterline) : null;

    currentSource = {
        wallCenterline,
        lumenCenterline,
        wallPerBranch: wallBuilt?.centerlinesPolyData ?? null,
        wallBranchIndices: wallBuilt?.branchOriginalIndices ?? null,
        wallMask,
        lumenMask,
        diameterCache: new Map(),
    };
}

/**
 * Copy point positions from the master polydata into the per-branch polydatas.
 * Orientation tensors are left unchanged so reslice frames stay consistent
 * with the orthogonal viewport, producing byte-identical diameters after
 * centerline edits.
 */
export function syncPerBranchPolyDatasFromMaster() {
    const s = currentSource;
    if (!s) return;
    syncBranchPoints(s.wallCenterline, s.wallPerBranch, s.wallBranchIndices);
    s.diameterCache.clear();
}

function syncBranchPoints(masterPolyData, perBranch, branchIndices) {
    if (!masterPolyData || !perBranch || !branchIndices) return;
    const masterPoints = masterPolyData.getPoints().getData();
    for (const branchName in perBranch) {
        const indices = branchIndices[branchName];
        const branchPolyData = perBranch[branchName];
        if (!indices || !branchPolyData) continue;
        const branchPoints = branchPolyData.getPoints().getData();
        const n = indices.length;
        for (let i = 0; i < n; i++) {
            const masterIdx = indices[i];
            branchPoints[3 * i] = masterPoints[3 * masterIdx];
            branchPoints[3 * i + 1] = masterPoints[3 * masterIdx + 1];
            branchPoints[3 * i + 2] = masterPoints[3 * masterIdx + 2];
        }
        branchPolyData.getPoints().modified();
        branchPolyData.modified();
    }
}

/**
 * Compute the stent-planning diameter at a branch-local index.
 * Returns null if sources aren't ready or the requested branch/centerlineType
 * has no polydata or mask volume.
 *
 * @param {string} branch - 'Left' | 'Right' | 'Cusp'.
 * @param {string} centerlineType - a CenterlineType value (WALL | LUMEN).
 * @param {number} branchIndex - branch-local index along that branch's polyline.
 * @param {ArrayLike<number>=} storedSliceBuffer - optional painted-slice buffer
 *        overlaid on the resliced mask before the diameter algorithm runs.
 */
export function computeMeasurement(branch, centerlineType, branchIndex, storedSliceBuffer) {
    const s = currentSource;
    if (!s) return null;

    const perBranch = s.wallPerBranch;
    const maskVolume = centerlineType === CenterlineType.LUMEN ? s.lumenMask : s.wallMask;
    if (!perBranch || !maskVolume) return null;

    const polyData = perBranch[branch];
    if (!polyData) return null;

    // Cache the null-buffer case (scrolling, panel/orthogonal labels, range
    // queries), whose result depends only on geometry + index.  A painted stored
    // buffer is slice-specific, so it always recomputes.
    const cacheKey = storedSliceBuffer == null ? `${branch}|${centerlineType}|${branchIndex}` : null;
    if (cacheKey !== null) {
        const cached = s.diameterCache.get(cacheKey);
        if (cached !== undefined) return cached;
    }

    const result = computeDiameterAtIndex({
        reslice: getSharedReslice(),
        maskVolume,
        polyData,
        index: branchIndex,
        storedBuffer: storedSliceBuffer,
    });

    if (cacheKey !== null && result != null) {
        s.diameterCache.set(cacheKey, result);
    }
    return result;
}

/**
 * Compute a diameter measurement given a master centerline index, converting
 * it to the corresponding branch and branch-local index before delegating to
 * `computeMeasurement`.  Resolves the centerline side by matching `masterPolyData`
 * against the active study's wall/lumen master polydata.
 */
export function computeMeasurementByMasterIndex(masterPolyData, masterIndex, storedSliceBuffer) {
    if (!masterPolyData || masterIndex == null || masterIndex < 0) return null;
    const s = currentSource;
    if (!s) return null;

    const isWall = s.wallCenterline === masterPolyData;
    const isLumen = s.lumenCenterline === masterPolyData;
    if (!isWall && !isLumen) return null;

    const conv = tryConvertMasterToDistanceBranchIndex(masterIndex, masterPolyData.getFieldData());
    if (!conv) return null;

    const centerlineType = isWall ? CenterlineType.WALL : CenterlineType.LUMEN;
    return computeMeasurement(conv.branch, centerlineType, conv.index, storedSliceBuffer);
}
